import React, { FunctionComponent, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';

import VerticalTabBar, {
  IVerticalTabItem,
} from '../../../../../../../components/V2/Tabs/vertical/type1/VerticalTabBar';
import { useAppSelector } from '../../../../../../../hooks';
import {
  getPenaltyWaiverById,
  getPenaltyWaiverState,
  resetPenaltyWaiverData,
} from '../../../../../../../store/V2/PenaltyWaiverSlice';
import BasicLoader from '../../../../../../../components/Loaders/BasicLoader';
import ClassDetailsTab from './Tabs/ClassDetailsTab';
import StudentsTab from './Tabs/StudentsTab';
import PenaltyAppealTab from './Tabs/PenaltyAppealTab';

interface ViewPenaltyWaiverModalProps {
  open: boolean;
  penaltyWaiverId: string;
  defaultTabIndex?: number;
  onClose: () => void;
}

const defaultTabs: IVerticalTabItem[] = [
  {
    index: 0,
    key: 'classDetails',
    title: 'Class Details',
  },
  {
    index: 1,
    key: 'students',
    title: 'Students',
  },
  {
    index: 2,
    key: 'penaltyAppeal',
    title: 'Penalty Appeal',
  },
];

export type TState = {
  selectedTab: IVerticalTabItem | null;
  tabs: IVerticalTabItem[];
};

const initialState: TState = {
  selectedTab: null,
  tabs: [],
};

const ViewPenaltyWaiverModal: FunctionComponent<ViewPenaltyWaiverModalProps> = ({
  open,
  defaultTabIndex = 0,
  penaltyWaiverId,
  onClose,
}) => {
  const dispatch = useDispatch();

  // Store
  const penaltyWaiversState = useAppSelector(getPenaltyWaiverState);

  // State
  const [state, setState] = useState({ ...initialState });
  const [initialLoading, setInitialLoading] = useState(true);

  const { penaltyWaiverData } = penaltyWaiversState;

  useEffect(() => {
    initialize();

    return () => {
      dispatch(resetPenaltyWaiverData());
    };
  }, []);

  useEffect(() => {
    if (penaltyWaiverData) {
      setTabs();
    }
  }, [penaltyWaiverData]);

  const updateStateAttributes = (attributes: Partial<TState>) => {
    setState((prevState) => ({
      ...prevState,
      ...attributes,
    }));
  };

  const setTabs = () => {
    const newTabs: IVerticalTabItem[] = [...defaultTabs].filter((item) => {
      if (item.key === 'penaltyAppeal') {
        return penaltyWaiverData && penaltyWaiverData.appealStatus;
      }

      return true;
    });

    let newSelectedTab: IVerticalTabItem | null = newTabs[0] || null;

    const defaultTabIndexItem = defaultTabs.find(
      (item) => item.index === defaultTabIndex
    );

    if (defaultTabIndexItem) {
      const matchingTab = newTabs.find(
        (item) => item.key === defaultTabIndexItem.key
      );

      if (matchingTab) {
        newSelectedTab = matchingTab;
      }
    }

    updateStateAttributes({
      tabs: newTabs,
      selectedTab: newSelectedTab,
    });
  };

  const initialize = async () => {
    await dispatch(getPenaltyWaiverById({ id: penaltyWaiverId }));

    setInitialLoading(false);
  };

  const renderTabPanel = () => {
    let tabPanel: JSX.Element | null = null;

    if (state.selectedTab && penaltyWaiverData) {
      switch (state.selectedTab.key) {
        case 'classDetails':
          tabPanel = <ClassDetailsTab penaltyWaiver={penaltyWaiverData} />;
          break;

        case 'students':
          tabPanel = <StudentsTab penaltyWaiver={penaltyWaiverData} />;
          break;

        case 'penaltyAppeal':
          tabPanel = <PenaltyAppealTab penaltyWaiver={penaltyWaiverData} />;
          break;

        default:
          tabPanel = null;
          break;
      }
    }

    return tabPanel;
  };

  return (
    <Dialog
      fullWidth
      maxWidth="lg"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { borderRadius: '20px', maxWidth: '850px' },
      }}
    >
      <DialogTitle
        sx={{
          m: 0,
          p: { xs: '15px', sm: '30px' },
          color: '#000000',
          fontWeight: 600,
          fontSize: '16px',
        }}
      >
        More Details
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={onClose}
        sx={{
          position: 'absolute',
          right: { xs: '7px', sm: '22px' },
          top: { xs: '7px', sm: '22px' },
        }}
      >
        <CloseIcon sx={{ color: '#000000' }} />
      </IconButton>
      <DialogContent
        sx={{ p: { xs: '0 15px 15px 15px', sm: '0 30px 30px 30px' } }}
      >
        {initialLoading ? (
          <div style={{ padding: '16px 40px' }}>
            <BasicLoader />
          </div>
        ) : null}
        {penaltyWaiverData ? (
          <div>
            <Grid container columnSpacing="24px" rowSpacing="16px">
              <Grid
                item
                xs={12}
                sm="auto"
                sx={{
                  width: { xs: '100%', sm: '200px' },
                  borderRight: '1px solid #CBD5E1',
                }}
              >
                <Box
                  sx={{
                    position: 'sticky',
                    top: 0,
                    minHeight: { xs: 'none', sm: '400px' },
                  }}
                >
                  <VerticalTabBar
                    selectedTab={state.selectedTab || undefined}
                    tabs={state.tabs}
                    onChange={(e, value) =>
                      updateStateAttributes({ selectedTab: state.tabs[value] })
                    }
                  />
                </Box>
              </Grid>
              <Grid item xs={12} sm>
                <div>{renderTabPanel()}</div>
              </Grid>
            </Grid>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};

export default ViewPenaltyWaiverModal;
